import React, { useEffect, useState } from 'react';
import { MdDelete } from 'react-icons/md';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { UserService } from '../UserService';
import { Card, Col, Row, Table } from 'react-bootstrap';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { UserDevices } from '../../../model/user/UserDevices';
import { IUser } from '../../../model/user/IUser';
import { Navigate, useLocation, useParams } from 'react-router';
import { FaDesktop, FaMobile, FaRegCopy } from 'react-icons/fa';
import { copyToClipboard } from '../../../utils/helpers/CopyToClipboard';
import { formatDate } from '../../../utils/Functions';
import EmptyState from '../../../shared/components/EmptyState';

export function UserDeviceList() {
    const location = useLocation();
    const { id } = useParams();
    const [user, setUser] = useState<IUser | undefined>(location.state?.user);
    const [triggerUpdate, setTriggerUpdate] = useState(false);
    const [state, setState] = useState<UserDevices[]>([]);
    const userId = user?.id ?? (id ? Number(id) : undefined);

    useEffect(() => {
        if (!user && id) {
            UserService.getUser(Number(id))
                .then(setUser)
                .catch(err => ErrorToast(err));
        }
    }, [id]);

    useEffect(() => {
        if (userId) {
            UserService.getUserDevices(userId)
                .then(data => {
                    setState(data);
                })
                .catch(err => ErrorToast(err));
        }
    }, [triggerUpdate, userId]);

    const handleDeleteDevice = async (deviceId: number) => {
        if (deviceId) {
            try {
                const response = await UserService.deleteUserDevices(deviceId);
                if (response) {
                    SuccessToast('Successfully deleted device');
                    setTriggerUpdate(prev => !prev);
                }
            } catch (err) {
                ErrorToast(err);
            }
        }
    };

    if (!userId) {
        return <Navigate to="/user" replace />;
    }

    return (
        <>
            <Row className="align-items-center mb-3">
                <Col className="col-auto">
                    <h3 className="m-0">
                        {user?.displayName
                            ? `ID: ${userId} Name: ${user.displayName}`
                            : `ID: ${userId} Name: ${user?.firstName ?? ''} ${user?.lastName ?? ''}`}
                    </h3>
                </Col>
                <Col className="col-auto">
                    <ImpersonateEye id={userId} isEnabled={user?.isEnabled} classes="btn-icon btn-primary" />
                </Col>
            </Row>

            <Card className="mb-5 p-4">
                {state.length === 0 ? (
                    <EmptyState classHolder="shadow-none" />
                ) : (
                    <Table responsive className="table table-row-dashed fs-7 gy-5 dataTable no-footer">
                        <thead>
                            <tr className="even">
                                <th>Device Id</th>
                                <th>Brand</th>
                                <th>Model</th>
                                <th>Type</th>
                                <th>Token</th>
                                <th>Token Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state?.map(item => (
                                <tr key={item.id}>
                                    <td>{item?.id}</td>
                                    <td>{item.brand ?? '-'}</td>
                                    <td>{item.model ?? 'N/A'}</td>
                                    <td>
                                        {['mobile', 'smartphone', 'phone'].includes(item.type) ? (
                                            <FaMobile />
                                        ) : (
                                            <FaDesktop />
                                        )}
                                    </td>
                                    <td>{item.token ?? 'N/A'}</td>
                                    <td>{item.token ? formatDate(String(item.createdAt)) : 'N/A'}</td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            {item.token && (
                                                <div
                                                    className="btn btn-icon btn-sm btn-light mr-2"
                                                    onClick={() => copyToClipboard(item.token)}
                                                >
                                                    <FaRegCopy className="text-muted" size={20} />
                                                </div>
                                            )}

                                            <div
                                                className="btn btn-icon btn-sm btn-danger"
                                                onClick={() => handleDeleteDevice(item?.id)}
                                            >
                                                <MdDelete size={25} />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card>
        </>
    );
}
